/**
 * The body of one navigation request: serialise it against any other request,
 * ask the revision policy whether it may proceed, resolve unsaved edits, and
 * run the caller's commit exactly once and only when the destination is still
 * the one the user asked for.
 *
 * Both the mounted guard and the fail-closed fallback used while no guard is
 * mounted run this same transaction, so there is one decision procedure and
 * not two that can drift apart.
 */

import {
  createNavigationIntent,
  resolveNavigationDecision,
  type NavigationIntent,
  type NavigationOutcome,
  type NavigationRequest,
} from '../../lib/setupRevision';
import { useUIStore } from '../../stores/useUIStore';

export interface NavigationCommitOptions {
  /**
   * Consent this destination needs beyond the unsaved-changes decision — for
   * example a profile load telling the user which setup parameters it will add.
   * It is asked while the intent is still pending, so a second control pressed
   * meanwhile is superseded instead of opening a second dialog.
   */
  confirm?: (intent: NavigationIntent) => Promise<boolean>;
}

/** Hand a destination and the action that carries it out; nothing else navigates. */
export type RequestNavigation = (
  request: NavigationRequest,
  commit: () => void,
  options?: NavigationCommitOptions
) => Promise<NavigationOutcome>;

export interface UnsavedEditResolution {
  /** Whether the screen the user is leaving holds unsaved edits. */
  isDirty: boolean;
  /** Asks the user what to do with those edits and carries the answer out. */
  resolveUnsaved: (intent: NavigationIntent) => Promise<NavigationOutcome>;
}

/** The destination the user asked for is no longer the pending one. */
const isSupersededIntent = (intent: NavigationIntent): boolean =>
  useUIStore.getState().pendingNavigationIntent?.id !== intent.id;

export const runNavigationTransaction = async (
  request: NavigationRequest,
  commit: () => void,
  { isDirty, resolveUnsaved }: UnsavedEditResolution,
  options?: NavigationCommitOptions
): Promise<NavigationOutcome> => {
  const { pendingNavigationIntent: alreadyPending, setupRevisionStatus } = useUIStore.getState();

  // One unresolved request at a time, whatever raised the second one.
  if (alreadyPending) {
    return 'superseded';
  }

  const intent = createNavigationIntent(request);
  const decision = resolveNavigationDecision(setupRevisionStatus, intent.kind);

  if (decision === 'block') {
    return 'blocked';
  }

  useUIStore.getState().beginNavigationIntent(intent);

  try {
    const outcome: NavigationOutcome =
      decision === 'prompt' || isDirty ? await resolveUnsaved(intent) : 'committed';

    if (outcome !== 'committed') {
      return outcome;
    }

    // A new revision opened while we were saving invalidates this destination.
    if (isSupersededIntent(intent)) {
      return 'superseded';
    }

    // Destination-specific consent (e.g. a profile load naming what it will add)
    // is raised through the guard, never via a browser-native popup.
    const requestConsent = options?.confirm;
    if (requestConsent && !(await requestConsent(intent))) {
      return 'cancelled';
    }

    if (isSupersededIntent(intent)) {
      return 'superseded';
    }

    commit();
    return 'committed';
  } finally {
    useUIStore.getState().endNavigationIntent(intent.id);
  }
};
